import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Glossary } from '../shared/models/glossary.model';
import { Translation } from '../shared/models/translation.model';
import { Subject, Observable } from 'rxjs';
import { tabellaGlossari } from './store';
import { Attachment } from '../shared/models/attachment.model';
import { FilterData } from '../shared/models/filter-data/filter-data.model';


export interface FilterDataGlossResponse extends FilterData {
  totalCount: number;
  data: Glossary[];
}

/**Enumerazione che rappresenta i diversi punti che possono chiamare il modale di ricerca glossario */
export enum cercaGlossariParent {
  modificaAssocia,
  aggiuntaVediAnche
}

/**Servizio che raggruppa i metodi che gestiscono le chiamate a BE per la sezione glossari */
@Injectable({
  providedIn: 'root'
})
export class GlossariService {
  /**Subject per la gestione del rimando a pagina iniziale */
  private subject = new Subject<any>();
  /**Subject per la gestione della visualizzazione del modale di ricerca glossario */
  private modalSubject = new Subject<{ valore: boolean, provenienza: cercaGlossariParent }>();

  /**
   * Costruttore per la classe GlossariService
   * @param http {HttpClient}
   */
  constructor(
    private http: HttpClient
  ) { }

  /**
   * Metodo che gestisce la chiamata a BE per il recupero dell'elenco delle voci di glossario 
   * @param body {any} un oggetto contente i parametri per paginare, filtrare e ordinare l'elenco di voci glossario richiesto
   */
  getListaGlossari(body: any) {
    return this.http.post<FilterDataGlossResponse>('service/glossary/list', body);
  }

  /**
   * Metodo che gestisce la chiamata a BE per l'aggiornamento di una voce di glossario
   * @param idGlos {number} id della voce di glossario da modificare
   * @param nomeCampo {string} nome della proprietà modificata
   * @param newValue {any} nuovo valore assunto dalla proprietà
   */
  updateGlossary(idGlos: number, nomeCampo: string, newValue: any) {
    return this.http.post<Glossary>(
      'service/glossary/' + idGlos + '/' + nomeCampo,
      newValue
    );
  }

  /**
   * Metodo che gestisce la GET a BE per il recupero dei riferimenti alla voce di glossario selezionata
   * @param idGlos {number} id della voce di glossario selezionata
   */
  getTranslations(idGlos: number) {
    return this.http.get<Translation[]>('service/glossary/' + idGlos + '/translations');
  }

  /**
   * Metodo che gestisce la GET a BE per il recupero dei see also per la voce di glossario selezionata
   * @param idGlos {number} ide della voce di glossario selezionata
   */
  getSeeAlso(idGlos: number) {
    return this.http.get<Glossary[]>('service/glossary/' + idGlos + '/seeAlso');
  }

  /** Metodo per lanciare il ritorno a pagina iniziale nella tabella glossari */
  sendGoToFirstPage(tabella: tabellaGlossari) {
    this.subject.next(tabella);
  }

  /**Metodo per recuperare l'evento di ritorno a pagina iniziale nella tabella glossari */
  getGoToFirstPage(): Observable<tabellaGlossari> {
    return this.subject.asObservable();
  }

  /**
   * Metodo che causa l'emissione di un evento di apertura modale di ricerca glossario
   * @param valore {boolean} se deve essere visibile o meno
   * @param provenienza {cercaGlossariParent} da quale area è stato chiamato il modale, ne influenza visualizzazione e modalità
   */
  setModalVisibility(valore: boolean, provenienza: cercaGlossariParent) {
    this.modalSubject.next({ valore: valore, provenienza: provenienza });
  }

  /**Metodo per recuperare l'evento di visualizzazione del modale cerca glossari */
  getModalVisibility(): Observable<{ valore: boolean, provenienza: cercaGlossariParent }> {
    return this.modalSubject.asObservable();
  }

  /**
   * Metodo che gestisce la chiamata a BE per aggiungere a un glossario una nuova voce see also
   * @param idGloss {number} id del glossario principale
   * @param idSeeAlso {number} id del glossario da aggiungere come vedi anche
   */
  addNewSeeAlso(idGloss: number, idSeeAlso: number) {
    return this.http.get('service/glossary/' + idGloss + '/seeAlso/add/' + idSeeAlso);
  }

  /**
   * Metodo che gestisce la chiamata a BE per rimuovere da un glossario una voce see also
   * @param idGlos {number} id del glossario principale
   * @param idSeeAlso {number} id del glossario da rimuovere come vedi anche
   */
  removeSeeAlso(idGlos: number, idSeeAlso: number) {
    return this.http.get('service/glossary/' + idGlos + '/seeAlso/remove/' + idSeeAlso);
  }

  /**
   * Metodo che gestisce la chiamata post per salvare una nuova voce di glossario
   * @param newGlossary {Glossary} la nuova voce di glossario
   * @returns Observable<Glossary>
   */
  addNewGlossary(newGlossary: Glossary): Observable<Glossary> {
    return this.http.post<Glossary>(
      'service/glossary/create',
      newGlossary
    );
  }

  /**
   * Metodo che gestisce la chiamata get per rimuovere un glossario
   * @param idGlos {number} id del glossario da rimuovere
   */
  removeGlossary(idGlos: number): Observable<Object> {
    return this.http.get('service/glossary/' + idGlos + '/remove');
  }

  /**
   * Metodo che gestisce la chiamata get per ripristinare una voce di glossario eliminata
   * @param idGlos {number} identificativo della voce di glossario
   */
  restoreGlossary(idGlos: number): Observable<Object> {
    return this.http.get('service/glossary/' + idGlos + '/restore');
  }

  /**
   * Metodo che gestisce la chiamata GET per rimuovere tutti i riferimenti a una voce
   * @param glossaryId {number} identificativo della voce di glossario
   */
  getDissociateAllReferences(glossaryId: number) {
    return this.http.get('service/glossary/' + glossaryId + '/dissociate', { responseType: "text" });
  }

  /**
   * Metodo che gestisce la chiamata GET per recuperare lo storico delle modifiche alla voce di glossario
   * @param glossaryId {number} identificativo della voce di glossario
   */
  getGlossaryHistory(glossaryId: number): Observable<Glossary[]> {
    return this.http.get<Glossary[]>('service/glossary/' + glossaryId + '/history')
  }

  /**
   * Metodo che gestisce la chiamata POST per aggiornare i campi di una voce di glossario
   * @param glossaryId {number} identificativo della voce di glossario
   * @param modifiedProp {any} un json di coppie chiave-valore dove la chiave è il nome del campo da modificare e il valore è il valore da associare
   */
  postGlossaryUpdate(glossaryId: number, modifiedProp: any): Observable<Glossary> {
    return this.http.post<Glossary>(
      'service/glossary/' + glossaryId + '/update',
      modifiedProp
    );
  }

  /**
   * Metodo che gestisce la chiamata POST per associare la voce di glossario a tutte le stringhe a cui sono associati i glossari richiesti
   * @param glossaryId {number} identificativo del glossario principale
   * @param secondaryGlosList {number[]} elenco di identificativi dei glossari i cui riferimenti sono da riassociare
   */
  postAssociateMultipleReferences(glossaryId: number, secondaryGlosList: number[]) {
    return this.http.post(
      'service/glossary/' + glossaryId + '/associate',
      secondaryGlosList,
      { responseType: "text" }
    );
  }

  /**
   * Metodo che gestisce la chiamata GET per recuperare la lista di glossari che riferiscono la voce come "vedi anche"
   * @param glossaryId {number} identificativo del glossario di cui si vogliono i glossari che riferiscono la voce
   */
  getSeeAlsoIncome(glossaryId: number): Observable<Glossary[]> {
    return this.http.get<Glossary[]>('service/glossary/' + glossaryId + '/seeAlso/income');
  }

  /**
   * Metodo che gestisce la chiamata GET per recuperare la lista di traduzioni dove occorrono i glossari che riferiscono la voce come "vedi anche"
   * @param glossaryId {number} identificativo del glossario
   */
  getSeeAlsoIncomeTranslations(glossaryId: number): Observable<Translation[]> {
    return this.http.get<Translation[]>('service/glossary/' + glossaryId + '/seeAlso/income/translations');
  }

  /**
   * Metodo che gestisce la chiamata GET per recuperare l'allegato associato a un glossario
   * @param glossaryId {number} identificativo del glossario
   * @returns l'allegato al glossario
   */
  getGlossaryAttachment(glossaryId: number): Observable<Attachment> {
    return this.http.get<Attachment>('service/glossary/' + glossaryId + '/attachment');
  }

  postDownloadGlossaryExport(body, type: string){
    return this.http.post('service/glossary/export/' + type, 
      body, 
      {
        responseType: 'blob' as 'json', 
        observe: 'response'
      });
  }
}
